<template>
  <Teleport to="body">
    <div class="modal-overlay">
      <div class="modal-content" @click.stop>
        <h2 class="modal-title">メールアドレス指定</h2>
        <!-- エラーメッセージの表示エリア -->
        <div
          v-if="errorMessage"
          class="error-message"
          style="white-space: pre-wrap"
        >
          {{ errorMessage }}
        </div>
        <!-- To フィールド -->
        <div class="email-section">
          <label class="email-label">To</label>
          <div class="email-inputs">
            <input
              v-for="(email, index) in addresses.to"
              :key="`to-${index}`"
              v-model="addresses.to[index]"
              type="email"
              class="email-input"
              placeholder="メールアドレスを入力"
              @blur="cleanupEmptyFields('to')"
            />
          </div>
        </div>

        <!-- Cc フィールド -->
        <div class="email-section">
          <label class="email-label">Cc</label>
          <div class="email-inputs">
            <input
              v-for="(email, index) in addresses.cc"
              :key="`cc-${index}`"
              v-model="addresses.cc[index]"
              type="email"
              class="email-input"
              placeholder="メールアドレスを入力"
              @blur="cleanupEmptyFields('cc')"
            />
          </div>
        </div>

        <!-- Bcc フィールド -->
        <div class="email-section">
          <label class="email-label">Bcc</label>
          <div class="email-inputs">
            <input
              v-for="(email, index) in addresses.bcc"
              :key="`bcc-${index}`"
              v-model="addresses.bcc[index]"
              type="email"
              class="email-input"
              placeholder="メールアドレスを入力"
              @blur="cleanupEmptyFields('bcc')"
            />
          </div>
        </div>

        <!-- ボタン -->
        <div class="button-group">
          <button @click="handleCancel" class="btn btn-cancel">
            通知をやめて登録/返信処理を完了する
          </button>
          <button @click="handleOk" class="btn btn-ok">決定</button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch, PropType } from "vue";
import {
  formatMessage,
  sanitizeInput,
  hasNoGaiji,
  commonHandleAxiosError,
} from "@/Common";
import messages from "@/assets/messages.json";
import apiAuthClient from "@/util/apiClientAuth";
import { AppConstants } from "@/constants/AppConstants";
import { useProcessingStore } from "../../store/useProcessingStore";

interface EmailAddresses {
  to: string[];
  cc: string[];
  bcc: string[];
}

export default defineComponent({
  name: "EmailAddressModal",
  props: {
    defaultAddresses: {
      type: Object as PropType<EmailAddresses>,
      default: () => ({
        to: [""],
        cc: [""],
        bcc: [""],
      }),
    },
  },
  emits: {
    ok: (addresses: EmailAddresses) => true,
    cancel: () => true,
  },
  setup(props, { emit }) {
    const processingStore = useProcessingStore();
    const errorMessage = ref("");
    // リアクティブデータ
    const addresses = ref<EmailAddresses>({
      to: [...props.defaultAddresses.to],
      cc: [...props.defaultAddresses.cc],
      bcc: [...props.defaultAddresses.bcc],
    });

    // 各セクションに必要な数の入力フィールドを確保（すべて5個に統一）
    const ensureMinimumFields = () => {
      const sections: (keyof EmailAddresses)[] = ["to", "cc", "bcc"];

      sections.forEach((section) => {
        const emails = addresses.value[section];
        const nonEmptyCount = emails.filter(
          (email) => email.trim() !== ""
        ).length;
        const totalNeeded = Math.max(nonEmptyCount + 1, 5); // すべて5個に統一

        // 足りない分を追加
        while (emails.length < totalNeeded) {
          emails.push("");
        }

        // 5個を超える場合は5個に制限
        if (emails.length > 5) {
          addresses.value[section] = emails.slice(0, 5);
        }
      });
    };

    // 空のフィールドをクリーンアップ（最後の空フィールドは残す）
    const cleanupEmptyFields = (section: keyof EmailAddresses) => {
      const emails = addresses.value[section];
      const nonEmptyEmails = emails.filter((email) => email.trim() !== "");

      // 非空のメールアドレス + 1つの空フィールドを保持（最大5個）
      const targetLength = Math.min(Math.max(nonEmptyEmails.length + 1, 5), 5);

      addresses.value[section] = [
        ...nonEmptyEmails,
        ...new Array(targetLength - nonEmptyEmails.length).fill(""),
      ];
    };

    // メールアドレスのバリデーション
    const validateEmail = (email: string): boolean => {
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const isHalfWidth = /^[\x20-\x7E]+$/;
      const sanitizedEmail = sanitizeInput(email);

      if (!isHalfWidth.test(sanitizedEmail)) {
        return false;
      } else if (!emailPattern.test(sanitizedEmail)) {
        return false;
      } else {
        errorMessage.value = "";
        return true;
      }
    };

    // 有効なメールアドレスがあるかチェック
    const hasValidEmail = computed(() => {
      return (
        addresses.value.to.some((email) => validateEmail(email)) ||
        addresses.value.cc.some((email) => validateEmail(email)) ||
        addresses.value.bcc.some((email) => validateEmail(email))
      );
    });

    /**
     * OKボタンクリック処理
     */
    const handleOk = async () => {
      if (processingStore.isProcessing) {
        return;
      }

      try {
        processingStore.isProcessing = true;
        errorMessage.value = "";
        // 空でないメールアドレスのみ抽出してサニタイジング
        const filteredAddresses: EmailAddresses = {
          to: addresses.value.to
            .filter((email) => email.trim() !== "")
            .map((email) => sanitizeInput(email)),
          cc: addresses.value.cc
            .filter((email) => email.trim() !== "")
            .map((email) => sanitizeInput(email)),
          bcc: addresses.value.bcc
            .filter((email) => email.trim() !== "")
            .map((email) => sanitizeInput(email)),
        };

        // 最低1つのメールアドレスが入力されているかチェック
        const totalEmails =
          filteredAddresses.to.length +
          filteredAddresses.cc.length +
          filteredAddresses.bcc.length;
        if (totalEmails === 0) {
          errorMessage.value = formatMessage(
            messages.ERR_0001,
            "メールアドレス"
          );
          return;
        }

        // メールアドレスの有効性チェック
        const invalidEmails: string[] = [];

        // 各セクションのメールアドレスをチェック
        (["to", "cc", "bcc"] as const).forEach((section) => {
          filteredAddresses[section].forEach((email) => {
            if (!validateEmail(email)) {
              invalidEmails.push(`${section.toUpperCase()}: ${email}`);
            }
          });
        });

        // 無効なメールアドレスがある場合はアラートを表示して処理中断
        if (invalidEmails.length > 0) {
          errorMessage.value = `以下のメールアドレスが正しくありません：\n${invalidEmails.join(
            "\n"
          )}`;
          return;
        }

        // 重複チェック
        const allEmails: string[] = [
          ...filteredAddresses.to,
          ...filteredAddresses.cc,
          ...filteredAddresses.bcc,
        ];

        const duplicates: string[] = [];
        const emailSet = new Set<string>();

        allEmails.forEach((email) => {
          const normalizedEmail = email.toLowerCase().trim();
          if (emailSet.has(normalizedEmail)) {
            if (!duplicates.includes(email)) {
              duplicates.push(email);
            }
          } else {
            emailSet.add(normalizedEmail);
          }
        });

        // 重複がある場合はアラートを表示して処理中断
        if (duplicates.length > 0) {
          errorMessage.value = `以下のメールアドレスが重複しています：\n${duplicates.join(
            "\n"
          )}`;

          return;
        }

        var agCode = ""; // AGコード
        var userId = ""; // ユーザーID
        var pseudoAgCode = ""; // 疑似ログインAGコード
        var pseudoUserId = ""; // 疑似ログインユーザーID
        var serchUserId = ""; // 検索用ユーザーID
        var serchAgCode = ""; // 検索用AGコード

        // ローカルストレージからユーザー情報を取得して内部変数に展開
        const userInfo = localStorage.getItem(AppConstants.USER_SESSION_NAME);
        if (userInfo) {
          const parsedUserInfo = JSON.parse(userInfo);
          agCode = parsedUserInfo.agCode;
          userId = parsedUserInfo.userId;
          pseudoAgCode = parsedUserInfo.pseudoAgCode;
          pseudoUserId = parsedUserInfo.pseudoUserId;

          if (pseudoUserId == "") {
            // 疑似ログインIDが空の場合は、疑似ログインしていないとみなす
            serchUserId = userId;
            serchAgCode = agCode;
          } else {
            serchUserId = pseudoUserId;
            serchAgCode = pseudoAgCode;
          }
        }
        // APIでドッペルゲンガーチェック
        const response = await apiAuthClient.post(
          "/api/ApplicationMgtKaunet/mailCheck",
          {
            systemKbn: process.env.VUE_APP_SYSTEM_KBN,
            userId: serchUserId,
            agCode: serchAgCode,
            to: addresses.value.to,
            cc: addresses.value.cc,
            bcc: addresses.value.bcc,
          }
        );

        // 確認ダイアログ表示
        const isConfirm = window.confirm(formatMessage(messages.CNF_0010));
        if (!isConfirm) {
          // いいえの場合
          return;
        }

        emit("ok", filteredAddresses);
      } catch (error: unknown) {
        // エラー処理
        handleAxiosError(error);
      } finally {
        // 処理中フラグOFF
        processingStore.isProcessing = false;
      }
    };

    // キャンセルボタンクリック処理
    const handleCancel = () => {
      emit("cancel");
    };

    // 入力フィールドを監視して動的に調整
    watch(addresses, ensureMinimumFields, { deep: false, immediate: true });

    // propsの変更を監視してaddressesを更新
    watch(
      () => props.defaultAddresses,
      (newAddresses) => {
        addresses.value = {
          to: [...newAddresses.to],
          cc: [...newAddresses.cc],
          bcc: [...newAddresses.bcc],
        };
      },
      { deep: true }
    );

    /**************************************
     * AxiosErrorを処理します。
     * @param error エラー情報
     *************************************/
    const handleAxiosError = (error: unknown) => {
      errorMessage.value = commonHandleAxiosError(error, false);
    };

    return {
      addresses,
      hasValidEmail,
      handleOk,
      handleCancel,
      cleanupEmptyFields,
      errorMessage,
    };
  },
});
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  padding: 24px;
  border-radius: 12px;
  min-width: 700px;
  max-width: 900px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
}

.modal-title {
  margin: 0 0 24px 0;
  color: #333;
  font-size: 18px;
  font-weight: 600;
}

.email-section {
  margin-bottom: 20px;
}

.email-label {
  display: inline-block;
  width: 60px;
  padding: 8px 12px;
  background-color: #f5f5f5;
  border: 1px solid #ddd;
  border-radius: 4px 0 0 4px;
  font-weight: 500;
  color: #555;
  vertical-align: top;
  text-align: center;
  min-height: 40px;
  box-sizing: border-box;
  line-height: 24px;
}

.email-inputs {
  display: inline-block;
  width: calc(100% - 60px);
  vertical-align: top;
  display: flex;
  flex-wrap: wrap;
  gap: 0;
}

.email-input {
  width: calc(33.333% - 1px);
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-left: none;
  font-size: 14px;
  box-sizing: border-box;
  min-height: 40px;
  margin: 0;
}

/* 最初の入力欄 */
.email-input:first-child {
  border-left: 1px solid #ddd;
}

/* 4番目の入力欄（2行目最初） */
.email-input:nth-child(4) {
  border-left: 1px solid #ddd;
  margin-top: 4px;
}

/* 5番目の入力欄（2行目2番目） */
.email-input:nth-child(5) {
  margin-top: 4px;
}

.email-input:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
  z-index: 1;
  position: relative;
}

.button-group {
  display: flex;
  gap: 12px;
  justify-content: center;
  margin-top: 32px;
}

.btn {
  padding: 12px 24px;
  border: 1px solid;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s;
  min-width: 120px;
}

.btn-cancel {
  background-color: white;
  color: #007bff;
  border-color: #007bff;
  min-width: 220px;
}

.btn-cancel:hover {
  background-color: #f8f9fa;
}

.btn-ok {
  background-color: #007bff;
  color: white;
  border-color: #007bff;
}

.btn-ok:hover:not(:disabled) {
  background-color: #0056b3;
}

.btn-ok:disabled {
  background-color: #ccc;
  border-color: #ccc;
  cursor: not-allowed;
}
</style>
